import type {
  MicroserviceChange,
  ReleaseDiff,
} from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import { DataTable } from '@sunstone/ui';
import type { ColumnDef } from '@tanstack/react-table';
import {
  imageTag,
  ReleaseHeaderBand,
  RepoLink,
  shortSha,
} from '@/components/ui/ReleaseManifestDisplay';

type DiffStatus = 'added' | 'changed' | 'removed';

interface DiffRow {
  status: DiffStatus;
  change: MicroserviceChange;
}

const STATUS_CHIP: Record<DiffStatus, { letter: string; className: string; label: string }> = {
  added: {
    letter: 'A',
    label: 'Added',
    className: 'text-green-600 dark:text-green-400 border-green-600/30 dark:border-green-400/30',
  },
  changed: {
    letter: 'M',
    label: 'Changed',
    className: 'text-amber-600 dark:text-amber-500 border-amber-600/30 dark:border-amber-500/30',
  },
  removed: {
    letter: 'D',
    label: 'Removed',
    className: 'text-destructive border-destructive/30',
  },
};

function DiffValue({
  oldValue,
  newValue,
  status,
}: {
  oldValue: string;
  newValue: string;
  status: DiffStatus;
}) {
  if (status === 'added') return <>{newValue || '—'}</>;
  if (status === 'removed') return <>{oldValue || '—'}</>;
  if (oldValue === newValue) return <>{newValue || '—'}</>;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-muted-foreground line-through">{oldValue || '—'}</span>
      <span className="text-muted-foreground">→</span>
      <span>{newValue || '—'}</span>
    </span>
  );
}

function StatusChip({ status }: { status: DiffStatus }) {
  const config = STATUS_CHIP[status];
  return (
    <span
      title={config.label}
      className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border font-mono text-[11px] font-bold ${config.className}`}
    >
      {config.letter}
    </span>
  );
}

function PlatformDBChangeRow({ change }: { change: MicroserviceChange }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <StatusChip status="changed" />
        <span className="truncate font-mono text-sm">{change.service}</span>
        <span className="shrink-0 text-xs text-muted-foreground">platform database</span>
        <RepoLink gitRepo={change.gitRepo} />
      </div>
      <span className="shrink-0 font-mono text-xs">
        <DiffValue
          oldValue={shortSha(change.oldCommitSha)}
          newValue={shortSha(change.newCommitSha)}
          status="changed"
        />
      </span>
    </div>
  );
}

const columns: ColumnDef<DiffRow>[] = [
  {
    id: 'status',
    header: '',
    enableSorting: false,
    accessorFn: (row) => row.status,
    meta: { widthPercent: 5 },
    cell: ({ row }) => <StatusChip status={row.original.status} />,
  },
  {
    id: 'service',
    header: 'Service',
    // Filters on service name, status, image tag, chart version, or commit
    // SHA - the cell below still only ever displays the service/chart name.
    accessorFn: (row) =>
      `${row.change.service} ${row.status} ${row.change.newDockerImage || row.change.oldDockerImage} ${row.change.newChartVersion || row.change.oldChartVersion} ${row.change.newCommitSha || row.change.oldCommitSha}`,
    sortingFn: (rowA, rowB) =>
      rowA.original.change.service.localeCompare(rowB.original.change.service),
    meta: { widthPercent: 32 },
    cell: ({ row }) => {
      const { change } = row.original;
      return (
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="truncate font-mono text-sm">{change.service}</span>
            <RepoLink gitRepo={change.gitRepo} />
          </div>
          {change.chartName && change.chartName !== change.service && (
            <p className="truncate font-mono text-xs text-muted-foreground">{change.chartName}</p>
          )}
        </div>
      );
    },
  },
  {
    id: 'image',
    header: 'Image tag',
    enableSorting: false,
    accessorFn: (row) => row.change.newDockerImage || row.change.oldDockerImage,
    meta: { widthPercent: 23 },
    cell: ({ row }) => (
      <span className="font-mono text-xs">
        <DiffValue
          oldValue={imageTag(row.original.change.oldDockerImage)}
          newValue={imageTag(row.original.change.newDockerImage)}
          status={row.original.status}
        />
      </span>
    ),
  },
  {
    id: 'chart',
    header: 'Chart version',
    enableSorting: false,
    accessorFn: (row) => row.change.newChartVersion || row.change.oldChartVersion,
    meta: { widthPercent: 23 },
    cell: ({ row }) => (
      <span className="font-mono text-xs">
        <DiffValue
          oldValue={row.original.change.oldChartVersion}
          newValue={row.original.change.newChartVersion}
          status={row.original.status}
        />
      </span>
    ),
  },
  {
    id: 'commit',
    header: 'Commit',
    enableSorting: false,
    accessorFn: (row) => row.change.newCommitSha || row.change.oldCommitSha,
    meta: { widthPercent: 17 },
    cell: ({ row }) => (
      <span className="font-mono text-xs text-muted-foreground">
        <DiffValue
          oldValue={shortSha(row.original.change.oldCommitSha)}
          newValue={shortSha(row.original.change.newCommitSha)}
          status={row.original.status}
        />
      </span>
    ),
  },
];

function DiffSummary({
  added,
  changed,
  removed,
}: {
  added: number;
  changed: number;
  removed: number;
}) {
  return (
    <div className="flex items-center gap-4 text-xs font-medium">
      {added > 0 && <span className="text-green-600 dark:text-green-400">{added} added</span>}
      {changed > 0 && <span className="text-amber-600 dark:text-amber-500">{changed} changed</span>}
      {removed > 0 && <span className="text-destructive">{removed} removed</span>}
    </div>
  );
}

export interface ReleaseDiffTableProps {
  diff?: ReleaseDiff;
  /** Shown when diff is present but has no added/changed/removed entries and
   * no platformDbChange. */
  emptyMessage?: string;
}

/**
 * A single git-diff-style table of every added/changed/removed microservice
 * (sorted alphabetically, tagged A/M/D) plus the shared xforge-db platform
 * component - shared by ReleaseDiffDialog (the promote/upgrade preview) and
 * ComponentsDiffPanel (the Workspace Detail "Components" tab).
 */
export function ReleaseDiffTable({ diff, emptyMessage }: ReleaseDiffTableProps) {
  const rows: DiffRow[] = diff
    ? [
        ...diff.added.map((change): DiffRow => ({ status: 'added', change })),
        ...diff.changed.map((change): DiffRow => ({ status: 'changed', change })),
        ...diff.removed.map((change): DiffRow => ({ status: 'removed', change })),
      ].sort((a, b) => a.change.service.localeCompare(b.change.service))
    : [];
  const hasChanges = rows.length > 0 || !!diff?.platformDbChange;

  return (
    <div className="space-y-4">
      <ReleaseHeaderBand release={diff?.release} />

      {!hasChanges && (
        <p className="text-sm text-muted-foreground py-4">
          {emptyMessage ?? 'No changes — this workspace is already up to date.'}
        </p>
      )}

      {diff?.platformDbChange && <PlatformDBChangeRow change={diff.platformDbChange} />}

      {rows.length > 0 && diff && (
        <DataTable
          columns={columns}
          data={rows}
          searchKey="service"
          searchPlaceholder="Search services…"
          pageSize={1000}
          enableSorting
          initialSorting={[{ id: 'service', desc: false }]}
          filterSlot={
            <DiffSummary
              added={diff.added.length}
              changed={diff.changed.length}
              removed={diff.removed.length}
            />
          }
        />
      )}
    </div>
  );
}
